import { Sidebar } from 'primereact/sidebar';
import { useEffect, type ReactNode } from 'react';

import { cn } from '@shared/lib/cn';

export interface BaseDrawerProps {
  visible: boolean;
  onHide: () => void;
  header: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** md = 520px (prototype default), wide = 720px, lg = 1200px (wide drawers). */
  size?: 'md' | 'wide' | 'lg';
  /** Accessible name when `header` isn't plain text. */
  label?: string | undefined;
}

const WIDTH = { md: '520px', wide: '720px', lg: '1200px' } as const;

/** Right-hand drawer used for view/edit forms. Esc closes; the whole body scrolls. */
export function BaseDrawer({
  visible,
  onHide,
  header,
  children,
  footer,
  size = 'md',
  label,
}: BaseDrawerProps) {
  // The page behind must not scroll while the drawer is open. PrimeReact's own lock is a
  // shared class on <body> that any inner dialog clears when it closes, so also hold one here.
  useEffect(() => {
    if (!visible) return undefined;
    const root = document.documentElement;
    const before = root.style.overflow;
    root.style.overflow = 'hidden';
    return () => {
      root.style.overflow = before;
    };
  }, [visible]);
  return (
    <Sidebar
      visible={visible}
      onHide={onHide}
      position="right"
      header={header}
      blockScroll
      // A modal side panel: announce it as a dialog, named by its title.
      role="dialog"
      pt={{
        root: {
          'aria-modal': true,
          'aria-label': label ?? (typeof header === 'string' ? header : undefined),
        },
      }}
      style={{ width: WIDTH[size], maxWidth: '100vw' }}
    >
      <div className="flex min-h-full flex-col">
        {/* Without a pinned footer the form's own buttons end the body: keep space below them. */}
        <div className={cn('flex-1', !footer && 'pb-5')}>{children}</div>
        {footer && (
          <div className="sticky bottom-0 -mx-5 mt-5 flex flex-wrap justify-end gap-2 border-t border-line bg-surface px-5 py-3">
            {footer}
          </div>
        )}
      </div>
    </Sidebar>
  );
}
